import type { AreaPane } from '@/views/lowcode/CFConfig';
import type {
  LIST_PAGE_SCHEMA,
  PAGE_ITEM_SCHEMA,
  PageCode,
  PageConfig,
  PageModalPayload,
  SchemaBasePageProps,
} from '../types';
import {
  getBaseBtnsConfig,
  getBaseFormConfig,
  getBaseModals,
  getBaseSearchConfig,
  getBaseTableConfig,
  getDataSearch,
  getDataTable,
  getHeaderBtns,
  getPageItemTabs,
  getPageModals,
  mergeJsonSchema,
} from './helper';

/** 转换页面配置成Schema */
export const SchemaConverter = {
  listPage: (config: PageConfig): LIST_PAGE_SCHEMA => {
    const mainArea = config.areaDTOList.filter((area) => area.areaType === 'DATA_TABLE')[0];

    return mergeJsonSchema(config.jsonSchema, {
      title: config.pageTitle,
      api: mainArea.api,
      modelId: mainArea.modelId!,
      headerBtns: getHeaderBtns(config.areaDTOList, mainArea.modelId),
      search: getDataSearch(config.areaDTOList, config.pageCode),
      table: getDataTable(config.areaDTOList, config.pageCode),
      modals: getPageModals(config.areaDTOList, config.pageCode),
      formList: getBaseForms(config.areaDTOList, config.pageCode),
      baseModals: getBaseModals(config.areaDTOList),
    });
  },
  pageItem: (config: PageConfig): PAGE_ITEM_SCHEMA => {
    const mainArea =
      config.areaDTOList.filter((area) => area.enable && area.areaType === 'PAGE_FORM')[0] || {};

    return mergeJsonSchema(config.jsonSchema, {
      title: config.pageTitle,
      modelId: mainArea.modelId!,
      api: mainArea.api,
      mainForm: getBaseFormConfig(mainArea, config.pageCode),
      headerBtns: getHeaderBtns(config.areaDTOList, mainArea.modelId),
      childTabs: getPageItemTabs(config.areaDTOList, config.pageCode),
      modals: getPageModals(config.areaDTOList, config.pageCode),
    });
  },
};
/** 转换页面配置成 Modal Schema */
export const ModalSchemaConverter = {
  listPage: (config: PageConfig): PageModalPayload['pageSchema'] => ({
    ...SchemaConverter.listPage(config),
    renderMode: 'MODAL_TABLE',
  }),
  pageItem: (config: PageConfig): PageModalPayload['pageSchema'] => ({
    ...SchemaConverter.pageItem(config),
    renderMode: 'MODAL_FORM',
  }),
};

const getBaseForms = (areas: AreaPane[], pageCode: PageCode) => {
  return areas
    .filter((area) => area.enable && area.areaType === 'BASE_FORM')
    .map((area) => getBaseFormConfig(area, pageCode));
};
const getBaseSearchs = (areas: AreaPane[], pageCode: PageCode) => {
  return areas
    .filter((area) => area.enable && area.areaType === 'BASE_SEARCH')
    .map((area) => getBaseSearchConfig(area, pageCode));
};
const getBaseTables = (areas: AreaPane[], pageCode: PageCode) =>
  areas
    .filter((area) => area.enable && ['BASE_TABLE', 'BASE_CARD'].includes(area.areaType))
    .map((area) => getBaseTableConfig(area, pageCode));
const getBaseBtns = (areas: AreaPane[]) =>
  areas
    .filter((area) => area.enable && area.areaType === 'BASE_BTNS')
    .map((area) => ({
      _ID: area.areaCode,
      buttons: getBaseBtnsConfig(area.buttons, () => ({ modelId: area.modelId })),
    }));

/** 基础组合模块转换器、自动解析 */
export const BaseSchemaConverter = (config: PageConfig) => {
  return mergeJsonSchema(config.jsonSchema, {
    version: config.version,
    formList: getBaseForms(config.areaDTOList, config.pageCode),
    searchList: getBaseSearchs(config.areaDTOList, config.pageCode),
    tableList: getBaseTables(config.areaDTOList, config.pageCode),
    btnList: getBaseBtns(config.areaDTOList),
    modalList: getPageModals(config.areaDTOList, config.pageCode),
    baseModals: getBaseModals(config.areaDTOList),
  }) as SchemaBasePageProps;
};
